import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query';

import api from '@src/utils/api';
import { INSIGHTS } from '@src/constants/endpoints';
import { FALLBACK_TEMPLATES } from '@src/constants/insights';

export const INSIGHTS_TEMPLATE_CATALOG_KEY = 'insightsTemplateCatalog';
export const CAMPAIGN_INSIGHTS_TEMPLATES_KEY = 'campaignInsightsTemplates';
export const MEDIA_INSIGHTS_SCORES_KEY = 'mediaInsightsScores';
export const CAMPAIGN_INSIGHTS_RANKINGS_KEY = 'campaignInsightsRankings';
export const INSIGHTS_CAMPAIGNS_KEY = 'insightsCampaigns';
export const RESPONSE_SUMMARY_KEY = 'insightsResponseSummary';

const retryUnlessAnswered = (failureCount, error) =>
  [403, 404].includes(error?.response?.status) ? false : failureCount < 3;

export const useInsightsTemplateCatalog = () =>
  useQuery(
    [INSIGHTS_TEMPLATE_CATALOG_KEY],
    () => api.get(`${INSIGHTS}/templates`)
      .then((body) => body.templates)
      .catch((error) => error?.response?.status === 404 ? FALLBACK_TEMPLATES : Promise.reject(error)),
    { staleTime: Infinity, retry: retryUnlessAnswered, placeholderData: FALLBACK_TEMPLATES }
  );

export const useCampaignInsightsTemplates = (campaignId) =>
  useQuery(
    [CAMPAIGN_INSIGHTS_TEMPLATES_KEY, campaignId],
    () => api.get(`${INSIGHTS}/campaigns/${campaignId}/templates`),
    { enabled: !!campaignId, retry: retryUnlessAnswered }
  );

// Saving the templates and flipping the switch both answer with the campaign's whole Insights state, and the
// two can be in flight at once: each answer only sets what its request changed, so whichever lands last cannot
// bring back the other one's old value.
const mergeCampaignInsights = (queryClient, campaignId, changed) =>
  queryClient.setQueryData([CAMPAIGN_INSIGHTS_TEMPLATES_KEY, campaignId], (current) => ({ ...current, ...changed }));

export const useSaveCampaignInsightsTemplates = (campaignId) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ templateKeys, customPromptText, dimensions }) => api.put(`${INSIGHTS}/campaigns/${campaignId}/templates`, { templateKeys, customPromptText, dimensions }),
    {
      onSuccess: (data) => {
        mergeCampaignInsights(queryClient, campaignId, { templateKeys: data.templateKeys, customPromptText: data.customPromptText, dimensions: data.dimensions });
        // Saving can add the campaign to, or drop it from, the campaigns with Insights.
        queryClient.invalidateQueries([INSIGHTS_CAMPAIGNS_KEY]);
      }
    }
  );
};

export const SUMMARY_POLL_MS = 3000;

// A response's AI summary: 404 until one is generated, which is not an error to retry. While another request is
// generating it, the answer says so and is asked again until the summary is there.
export const useResponseSummary = (submissionId) =>
  useQuery([RESPONSE_SUMMARY_KEY, submissionId], () => api.get(`${INSIGHTS}/responses/${submissionId}/summary`), {
    enabled: !!submissionId,
    retry: retryUnlessAnswered,
    // A failed refetch keeps the old "generating" data: polling stops on the error instead of repeating it.
    refetchInterval: (data, query) => (data?.status === 'generating' && query.state.status !== 'error' ? SUMMARY_POLL_MS : false)
  });

export const useGenerateResponseSummary = (submissionId) => {
  const queryClient = useQueryClient();
  return useMutation(() => api.post(`${INSIGHTS}/responses/${submissionId}/summary`), {
    onSuccess: (data) => queryClient.setQueryData([RESPONSE_SUMMARY_KEY, submissionId], data)
  });
};

// Off pauses the campaign's evaluations.
export const useSetCampaignInsightsEnabled = (campaignId) => {
  const queryClient = useQueryClient();
  return useMutation(
    (enabled) => api.put(`${INSIGHTS}/campaigns/${campaignId}/enabled`, { enabled }),
    { onSuccess: (data) => mergeCampaignInsights(queryClient, campaignId, { enabled: data.enabled }) }
  );
};

// Campaigns with Insights templates saved, most recently configured first, in the campaign picker format.
export const useInsightsCampaigns = () =>
  useQuery([INSIGHTS_CAMPAIGNS_KEY], () => api.get(`${INSIGHTS}/campaigns`), {
    select: (body) => (body?.data || []).map((campaign) => ({
      value: `${campaign.id}`,
      label: campaign.name,
      campaignLink: campaign.campaignLink || null
    }))
  });

export const useMediaInsightsScores = (mediaId) =>
  useQuery([MEDIA_INSIGHTS_SCORES_KEY, mediaId], () => api.get(`${INSIGHTS}/scores/${mediaId}`), {
    enabled: !!mediaId,
    retry: retryUnlessAnswered,
  });

// The scores of every video of a response, one query each, sharing useMediaInsightsScores' cache.
export const useResponseInsightsScores = (mediaIds) =>
  useQueries(mediaIds.map((mediaId) => ({
    queryKey: [MEDIA_INSIGHTS_SCORES_KEY, mediaId],
    queryFn: () => api.get(`${INSIGHTS}/scores/${mediaId}`),
    retry: retryUnlessAnswered,
  })));

export const useCampaignInsightsRankings = (campaignId) =>
  useQuery([CAMPAIGN_INSIGHTS_RANKINGS_KEY, campaignId], () => api.get(`${INSIGHTS}/scores?campaignId=${campaignId}`), {
    enabled: !!campaignId,
    retry: retryUnlessAnswered,
  });

// Must not exceed MAX_RECORD_LIMIT in the API (api/v1/pagination/PagingAndSorting.js): above it the
// API does not reject the request, it silently answers with 25 records.
export const RANKING_PAGE_SIZE = 250;
// 80 pages = 20,000 submissions: a guard against an API that ignores offset, not a product limit.
export const RANKING_MAX_PAGES = 80;

// The ranking can list any evaluated video of the campaign, so every page is needed to name each row.
export const fetchAllCampaignSubmissions = async (campaignId) => {
  const submissions = [];
  for (let pageIndex = 0; pageIndex < RANKING_MAX_PAGES; pageIndex += 1) {
    const offset = pageIndex * RANKING_PAGE_SIZE;
    const body = await api.get(`/v1/campaign-submissions?campaignIds=${campaignId}&limit=${RANKING_PAGE_SIZE}&offset=${offset}`);
    const page = body?.data || [];
    submissions.push(...page);
    if (page.length < RANKING_PAGE_SIZE) break;
    if (pageIndex === RANKING_MAX_PAGES - 1) {
      throw new Error(`Campaign ${campaignId} has more than ${RANKING_PAGE_SIZE * RANKING_MAX_PAGES} submissions; ranking cannot be loaded safely.`);
    }
  }
  return submissions;
};

export const useCampaignSubmissionsForRanking = (campaignId) =>
  useQuery(['insightsRankingSubmissions', campaignId], () => fetchAllCampaignSubmissions(campaignId), {
    enabled: !!campaignId,
    staleTime: 60000,
  });



// WEBPACK FOOTER //
// ./src/hooks/react-query/useInsightsEvaluationData.js